import { Radio, RadioGroup } from "@headlessui/react";

import { JSX } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { UpdateProfileForm } from "@/pages/gate/UpdateProfilePage.tsx";

import Button from "@/components/buttons/Button.tsx";
import { UpdateProfileProps, UpdateProfileTab } from "@/components/gate/update-profile/types.ts";
import UpdateProfileLayout from "@/components/layout/UpdateProfileLayout.tsx";

import { useProfileLanguages } from "@/hooks/ui/useProfileLanguages.ts";
import { cn } from "@/utils/cn.ts";

interface InterfaceLangTabProps extends Pick<UpdateProfileProps, "setTab"> {}

const InterfaceLangTab = ({ setTab }: InterfaceLangTabProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<UpdateProfileForm>();
  const langs = useProfileLanguages();

  const onSubmit = handleSubmit(() => {
    setTab(UpdateProfileTab.FORM);
  });

  return (
    <UpdateProfileLayout title={t("Profile.profile-language")} onBackClick={() => setTab(UpdateProfileTab.FORM)}>
      <p className={"mt-4 text-lg/6 max-md:self-start max-md:text-sm/4"}>{t("Profile.profile-language-message")}</p>
      <form onSubmit={onSubmit} className={"mt-6 w-full max-w-[27.125rem] max-md:mt-2 max-md:max-w-none"}>
        <Controller
          name={"interface_lang"}
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <RadioGroup {...field} aria-label={t("Profile.profile-language")} className={"flex flex-col gap-3"}>
              {langs.map((lang) => (
                <Radio
                  key={lang.id}
                  as={"button"}
                  type={"button"}
                  value={lang.value}
                  aria-label={lang.text}
                  className={"bg-surface-container flex items-center gap-3 rounded-2xl p-4 max-md:py-3"}
                >
                  <span
                    className={cn(
                      "flex size-6 items-center justify-center rounded-full p-1.5 outline outline-1 -outline-offset-1 transition-outline max-md:size-5",
                      { "outline-orange-1": field.value === lang.value, "outline-red-1": errors.interface_lang },
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block size-3 rounded-full bg-transparent transition-colors max-md:size-2",
                        field.value === lang.value && "bg-orange-1",
                      )}
                    />
                  </span>
                  {lang.text}
                </Radio>
              ))}
            </RadioGroup>
          )}
        />
        <div className={"mt-6 flex justify-end max-md:mt-3"}>
          <Button
            type={"submit"}
            aria-label={t("Actions.continue")}
            size="medium"
            className={"w-[14.375rem] max-md:py-4 max-sm:w-full"}
          >
            {t("Actions.continue")}
          </Button>
        </div>
      </form>
    </UpdateProfileLayout>
  );
};

export default InterfaceLangTab;
